// @flow
import type { AppState, ID } from '../core/types'
import type { Charity } from './charity/types'
import type { EntityMap } from '../modules/entities/types'
import values from 'lodash/values'
import { domains } from './constants'
import { BID_STATE } from './bid/constants'
import { getById, getDomain } from '../modules/entities'
import { hget } from '../modules/hash'
import { getList } from '../modules/list'
import { createSelector } from 'reselect'
import { getCharity } from './charity/selectors'
import { getImageSource } from './utils'
import { getWinningItemIds, getOutbidItemIds } from './bid'

export const getActiveEntities = (domain: string, state: AppState): any =>
  getList(domain, state)

export const getActiveEntity = (domain: string): any =>
  createSelector(
    hget(domain, 'activeId'),
    getList(domain),
    (activeEntity, activeEntities) => {
      return activeEntity || (activeEntity ? activeEntities[0] : undefined)
    }
  )

export const getEntity = (domain: string, id: ID, state: AppState) => {
  switch (domain) {
    case domains.ITEMS:
      return getItem(id)(state)
    case domains.AUCTIONS:
      return getAuction(id)(state)
    case domains.CHARITIES:
      return getCharity(id)(state)
    default:
      return getById(domain, id, state)
  }
}
type Item = {
  id: ID,
  auctionId: number,
  bids: Array<number>,
  media: Array<number>,
  currentBidId: ID
}
type Media = {
  id: number,
  source: string
}
export const getItem = (id: ID) =>
  createSelector(
    getById(domains.ITEMS, id),
    getDomain(domains.AUCTIONS),
    getDomain(domains.BIDS),
    getDomain(domains.MEDIA),
    getWinningItemIds,
    getOutbidItemIds,
    (
      item: Item,
      auctionsById: EntityMap<Auction>,
      bidsById: EntityMap<Object>,
      mediaById: EntityMap<Media>,
      winningItemsIds: ID[],
      outbidItemsIds: ID[]
    ) => {
      return item
        ? {
            ...item,
            auction: auctionsById[String(item.auctionId)],
            bids: (item.bids || []).map(id => bidsById[id.toString()]),
            currentBid: item.currentBidId
              ? bidsById[item.currentBidId.toString()]
              : null,
            media: (item.media || []).map(id =>
              getImageSource(mediaById[id.toString()].source)
            ),
            bidState: winningItemsIds.includes(item.id)
              ? BID_STATE.WINNING
              : BID_STATE.OUTBID
          }
        : null
    }
  )
type Auction = {
  id: ID,
  charityId: ID,
  photo: ?string,
  photo_thumb: ?string,
  items: ?Array<ID>
}

export const getAuction = (id: ID) =>
  createSelector(
    getById(domains.AUCTIONS, id),
    getDomain(domains.CHARITIES),
    getDomain(domains.ITEMS),
    (
      auction: Auction,
      charitiesById: EntityMap<Charity>,
      itemsById: EntityMap<Item>
    ) => {
      return auction
        ? {
            ...auction,
            photo: getImageSource(auction.photo),
            photo_thumb: getImageSource(auction.photo_thumb),
            charity:
              auction.charityId && charitiesById[String(auction.charityId)],
            items:
              auction.items && auction.items.map(id => itemsById[String(id)])
          }
        : null
    }
  )

export const getAuctionItems = (auctionId: number, state: AppState) => {
  const items: Array<Object> = values(getDomain(domains.ITEMS, state))
  return items.filter((i: Item) => i.auctionId === auctionId)
}

export const getUserBid = (state: AppState): any =>
  hget(domains.ITEMS, 'userBid', state)

export const hasMoreEntitiesToFetch = (
  domain: string,
  state: AppState
): boolean => {
  return Boolean(hget(domain, 'hasMore', state))
}

export const getCurrentPage = (domain: string, state: AppState) =>
  hget(domain, 'currentPage', state) || 0



// WEBPACK FOOTER //
// ./src/services/selectors.js